import React, { useState, useEffect } from 'react';

// カラーパレット
const C = {
  bg: '#FFFFFF',
  text: '#1A1A1A',
  accent: '#00A896',
  cta: '#FF8C00',
  bubble: '#F5F5F5',
  border: '#E8E8E8',
  headerBg: 'rgba(255,255,255,0.97)',
};

// ウィンドウ幅を監視するフック
function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return width;
}

// ヘッダー固定追従
function Header({ isPC }) {

  return (
    <header style={{
      position: 'sticky',
      top: 0,
      zIndex: 1000,
      background: C.headerBg,
      borderBottom: '1px solid #000000',
    }}>
      <div style={{
        maxWidth: isPC ? 760 : 480,
        margin: '0 auto',
        padding: isPC ? '18px 40px' : '14px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <img
          src="/images/nuntlogo.png"
          alt="Nunt"
          style={{ height: isPC ? 64 : 52, width: 'auto' }}
        />
        <a href="/lp02/apply" style={{
          background: C.cta,
          color: '#FFFFFF',
          fontSize: isPC ? 14 : 12,
          fontWeight: 700,
          padding: isPC ? '10px 24px' : '8px 16px',
          borderRadius: 20,
          textDecoration: 'none',
          letterSpacing: '0.03em',
          whiteSpace: 'nowrap',
        }}>
          เริ่มใช้งานฟรี
        </a>
      </div>
    </header>
  );
}

// シーン画像（実画像 or プレースホルダー）
function SceneImage({ src, label, isPC }) {
  if (src) {
    return (
      <img
        src={src}
        alt={label || ''}
        style={{
          width: '100%',
          display: 'block',
          borderRadius: isPC ? 4 : 0,
        }}
      />
    );
  }
  return (
    <div style={{
      width: '100%',
      paddingTop: '100%',
      position: 'relative',
      background: '#E8E8E8',
      borderRadius: isPC ? 4 : 0,
    }}>
      <div style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
      }}>
        <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#C8C8C8' }} />
        <span style={{ fontSize: 12, color: '#999', letterSpacing: '0.05em' }}>
          {label || 'IMAGE'}
        </span>
      </div>
    </div>
  );
}

// 丸いアバターアイコン＋名前
function Avatar({ name, src, isPC }) {
  const size = isPC ? 60 : 50;
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 4,
      minWidth: size,
      marginTop: 2,
    }}>
      {src ? (
        <img
          src={src}
          alt={name}
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            objectFit: 'cover',
            objectPosition: 'center top',
          }}
        />
      ) : (
        <div style={{
          width: size,
          height: size,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #c9b8e8 0%, #a78bcc 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: isPC ? 16 : 14,
        }}>
          🌸
        </div>
      )}
      <span style={{
        fontSize: 11,
        color: '#888888',
        textAlign: 'center',
        lineHeight: 1.3,
        whiteSpace: 'nowrap',
      }}>
        {name}
      </span>
    </div>
  );
}

// LINE風吹き出し（アイコン付き行）
function Bubble({ children, name, avatarSrc, isPC }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: isPC ? 10 : 16,
      width: '100%',
    }}>
      <Avatar name={name} src={avatarSrc} isPC={isPC} />
      <div style={{
        display: 'inline-block',
        maxWidth: '84%',
        background: C.bubble,
        borderRadius: '4px 12px 12px 12px',
        padding: isPC ? '16px 20px' : '12px 16px',
        fontSize: isPC ? 17 : 15,
        lineHeight: 1.9,
        color: C.text,
      }}>
        {children}
      </div>
    </div>
  );
}

// 吹き出しグループ
function BubbleGroup({ lines, names, avatarSrcs, isPC }) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: isPC ? 14 : 10,
      padding: isPC ? '0' : '0 20px',
    }}>
      {lines.map((line, i) => (
        <Bubble key={i} name={names[i]} avatarSrc={avatarSrcs?.[i]} isPC={isPC}>{line}</Bubble>
      ))}
    </div>
  );
}

// シーン共通レイアウト
function Scene({ title, isH1, imagelabel, imagesrc, bubbles, names, avatarSrcs, isPC }) {
  const titleStyle = {
    fontSize: isPC ? 28 : 22,
    fontWeight: 700,
    lineHeight: 1.5,
    color: C.text,
    letterSpacing: '0.02em',
    marginBottom: isPC ? 32 : 20,
  };
  const accentBar = (
    <div style={{
      width: 36,
      height: 2,
      background: C.accent,
      marginTop: 12,
      borderRadius: 1,
    }} />
  );

  // PC・スマホ共通：タイトル → 画像全幅 → 吹き出しの縦積み
  return (
    <section style={{ marginBottom: isPC ? 96 : 64 }}>
      <div style={{ padding: isPC ? '0' : '0 20px', marginBottom: isPC ? 32 : 24 }}>
        {isH1
          ? <h1 style={titleStyle}>{title}</h1>
          : <h2 style={titleStyle}>{title}</h2>}
        {accentBar}
      </div>
      <div style={{ marginBottom: isPC ? 36 : 28 }}>
        <SceneImage src={imagesrc} label={imagelabel} isPC={isPC} />
      </div>
      <BubbleGroup lines={bubbles} names={names} avatarSrcs={avatarSrcs} isPC={isPC} />
    </section>
  );
}

// Nuntとは説明ボックス（アコーディオン）
function NuntInfoBox({ isPC }) {
  const [openIndex, setOpenIndex] = useState(null);

  const items = [
    {
      icon: '📷',
      title: 'การถ่ายทำใหม่และเวลาในการทำงานเป็น "ศูนย์"',
      body: 'คุณไม่จำเป็นต้องไปที่สตูดิโอถ่ายภาพ หรือเตรียมเสื้อผ้าใหม่ใดๆ ทั้งสิ้น เพียงแค่ลงทะเบียนภาพถ่ายที่ดีที่สุดหรือภาพพอร์ตโฟลิโอที่มีอยู่กับ Nunt สิทธิ์ในภาพลักษณ์ของคุณก็จะเริ่มทำงานแทนคุณทันที',
      pastel: 'rgba(0, 168, 150, 0.07)',
    },
    {
      icon: '🤝',
      title: 'ปล่อยให้เรื่องสัญญาและการต่อรองที่ยุ่งยากเป็นหน้าที่ของเรา',
      body: 'Nunt จะช่วยบริหารจัดการโครงการโฆษณาบนเว็บไซต์ของธุรกิจขนาดกลางและขนาดย่อมที่ก่อนหน้านี้คุณอาจไม่สามารถรับได้เนื่องจากข้อจำกัดด้านเวลา โดยเราจะดำเนินการแทนคุณทั้งหมด ตั้งแต่งานเอกสารที่น่าปวดหัวไปจนถึงการต่อรองราคา เพื่อให้คุณสามารถโฟกัสกับงานหลักของคุณได้อย่างเต็มที่',
      pastel: 'rgba(255, 140, 0, 0.07)',
    },
    {
      icon: '🛡️',
      title: 'อุ่นใจด้วยการคัดกรองบริษัทอย่างเข้มงวดและการตรวจสอบขั้นสุดท้าย',
      body: 'Nunt จะทำการคัดกรองและตรวจสอบบริษัทลูกค้าอย่างเข้มงวดล่วงหน้า นอกจากนี้ คุณยังสามารถทำการตรวจสอบขั้นสุดท้ายสำหรับภาพที่จะถูกนำไปใช้จริงได้ด้วยตัวเอง จึงไม่ต้องกังวลว่าภาพลักษณ์ของคุณจะเสียหายโดยไม่รู้ตัว',
      pastel: 'rgba(0, 168, 150, 0.07)',
    },
    {
      icon: '🚀',
      title: 'สะสมผลงานเพื่อโอกาสในการถูกคัดเลือกเข้าสู่โฆษณาแบรนด์ใหญ่',
      body: 'ผลลัพธ์ที่ดีจากการโฆษณาบนเว็บไซต์จะถูกสะสมเป็นข้อมูล (Data) หากคุณได้รับความนิยม ข้อมูลนี้จะเป็นอาวุธสำคัญในการก้าวไปสู่ระดับที่สูงขึ้น เช่น การได้รับเลือกในงานวิดีโอที่มีมูลค่าสูง หรือการถูกเสนอชื่อโดยตรงจากแบรนด์ระดับประเทศ (National Brand)',
      pastel: 'rgba(255, 140, 0, 0.07)',
    },
  ];

  const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

  return (
    <div style={{ margin: isPC ? '0 0 64px' : '0 20px 48px' }}>
      {/* イントロボックス */}
      <div style={{
        border: `1.5px solid ${C.border}`,
        padding: isPC ? '28px 32px' : '22px 24px',
        marginBottom: isPC ? 32 : 24,
      }}>
        <p style={{
          margin: 0,
          fontSize: isPC ? 22 : 18,
          fontWeight: 700,
          color: C.text,
          letterSpacing: '0.03em',
          marginBottom: 10,
        }}>
          Nunt คืออะไร?
        </p>
        <div style={{ width: 36, height: 2, background: C.accent, borderRadius: 1, marginBottom: 16 }} />
        <p style={{
          margin: 0,
          fontSize: isPC ? 15 : 14,
          lineHeight: 1.95,
          color: '#666666',
        }}>
          แค่ปล่อยเช่าพอร์ตโฟลิโอของคุณ รายได้และโอกาสใหม่ๆ ก็จะเริ่มต้นขึ้นโดยอัตโนมัติ บริการสมัครสมาชิกสิทธิ์ในภาพลักษณ์ (Portrait Rights Subscription) สำหรับ Talent ยุคใหม่
        </p>
      </div>

      {/* タイトルラベル */}
      <div style={{
        display: 'inline-block',
        border: '2px solid #888888',
        padding: isPC ? '10px 22px' : '8px 18px',
        boxShadow: '4px 4px 0 #AAAAAA',
        marginLeft: 2,
        marginBottom: 16,
      }}>
        <span style={{
          fontSize: isPC ? 16 : 14,
          fontWeight: 700,
          letterSpacing: '0.06em',
          color: '#555555',
        }}>
          ☑ 4 จุดเด่นของ Nunt
        </span>
      </div>

      {/* アコーディオンリスト */}
      <div style={{ border: `1.5px solid ${C.border}`, overflow: 'hidden' }}>
        {items.map((item, i) => {
          const isOpen = openIndex === i;
          return (
            <div key={i} style={{ borderBottom: i < items.length - 1 ? `1px solid ${C.border}` : 'none' }}>
              {/* ヘッダー行 */}
              <button
                onClick={() => toggle(i)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: isPC ? 14 : 10,
                  padding: isPC ? '20px 28px' : '16px 20px',
                  background: isOpen ? item.pastel : '#FFFFFF',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background 0.25s ease',
                }}
              >
                <span style={{
                  fontWeight: 700,
                  fontSize: isPC ? 15 : 14,
                  color: C.accent,
                  minWidth: 20,
                  flexShrink: 0,
                }}>{i + 1}.</span>
                <span style={{ fontSize: isPC ? 15 : 14, flexShrink: 0 }}>{item.icon}</span>
                <span style={{
                  flex: 1,
                  fontSize: isPC ? 15 : 14,
                  fontWeight: 600,
                  color: C.text,
                  lineHeight: 1.6,
                  letterSpacing: '0.02em',
                }}>
                  {item.title}
                </span>
                <span style={{
                  fontSize: 11,
                  color: '#BBBBBB',
                  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.25s ease',
                  flexShrink: 0,
                }}>▼</span>
              </button>

              {/* 展開エリア */}
              <div style={{
                maxHeight: isOpen ? '400px' : '0',
                opacity: isOpen ? 1 : 0,
                overflow: 'hidden',
                transition: 'max-height 0.35s ease, opacity 0.28s ease',
              }}>
                <p style={{
                  margin: 0,
                  padding: isPC ? '4px 28px 24px 62px' : '4px 20px 20px 44px',
                  fontSize: isPC ? 14 : 13,
                  lineHeight: 1.95,
                  color: '#666666',
                }}>
                  {item.body}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// CTAセクション
function CTASection({ isPC }) {
  return (
    <section id="cta" style={{
      background: C.bubble,
      padding: isPC ? '72px 60px' : '52px 20px',
      textAlign: 'center',
      borderRadius: isPC ? 4 : 0,
      marginBottom: isPC ? 0 : 0,
    }}>
      <h2 style={{
        fontSize: isPC ? 24 : 20,
        fontWeight: 700,
        color: C.text,
        marginBottom: 12,
        lineHeight: 1.6,
      }}>
        เริ่มสร้าง Passive Income ของคุณตั้งแต่วันนี้
      </h2>
      <p style={{
        fontSize: isPC ? 16 : 14,
        color: '#777',
        marginBottom: 36,
        lineHeight: 1.8,
      }}>
        ไม่ต้องเสียเวลาต่อรองหรือไปถ่ายงานใหม่ แค่ใช้พอร์ตที่มีอยู่แล้วให้เช่าเท่านั้น
      </p>
      <a
        href="/lp02/apply"
        style={{
          display: 'block',
          width: '100%',
          background: C.cta,
          color: '#FFFFFF',
          fontSize: isPC ? 18 : 16,
          fontWeight: 700,
          padding: isPC ? '20px 0' : '18px 0',
          borderRadius: 8,
          textDecoration: 'none',
          letterSpacing: '0.05em',
        }}
      >
        เริ่มใช้งานฟรี
      </a>
    </section>
  );
}

// フッター
function Footer({ isPC }) {
  return (
    <footer style={{
      padding: isPC ? '32px 0' : '24px 20px',
      textAlign: 'center',
      borderTop: `1px solid ${C.border}`,
    }}>
      <p style={{ fontSize: 12, color: '#999', letterSpacing: '0.04em' }}>
        © 2026 Patlush Co., Ltd.
      </p>
    </footer>
  );
}

// セクション区切り線
function Divider({ isPC }) {
  return (
    <div style={{
      height: 1,
      background: C.border,
      margin: isPC ? `0 0 96px` : `0 20px 64px`,
    }} />
  );
}

// メインLP
export default function NuntLP02() {
  const width = useWindowWidth();
  const isPC = width > 768;

  const scenes = [
    {
      isH1: true,
      title: 'Smart Passive Income for Talent: เปลี่ยนภาพพอร์ตเดิมๆ ให้เป็นรายได้ที่ไหลเข้ามาไม่หยุด',
      imagelabel: 'SCENE 01',
      imagesrc: '/images/scene01.png',
      bubbles: [
        'วันนี้งานก็สนุกดีนะ ถ่ายแบบเสร็จแล้วก็มานั่งพักในคาเฟ่ที่ชอบ รู้สึกว่าชีวิตช่วงนี้กำลังไปได้สวยเลย ✨',
        'แต่พอมองดูรูปสวยๆ ในมือถือ ก็แอบคิดไม่ได้ว่า... งานแบบนี้จะอยู่กับเราไปได้อีกนานแค่ไหนนะ?',
      ],
      names: ['ตัวฉัน (ปัจจุบัน)', 'ตัวฉัน (ปัจจุบัน)'],
      avatarSrcs: ['/images/avatar-current.png', '/images/avatar-current.png'],
    },
    {
      title: 'เมื่อตัวคุณในอนาคตกลับมาหา... พร้อมคำถามสำคัญเกี่ยวกับความฝัน',
      imagelabel: 'SCENE 02',
      imagesrc: '/images/scene02.png',
      bubbles: [
        'สวัสดีจ๊ะ! ไม่ต้องตกใจนะ ฉันคือตัวคุณเองในอีก 3 ปีข้างหน้าเองจ้า 🎉 ลองทายดูซิ... ความฝันที่อยากไปเที่ยวปารีสตอนสมัยเรียนน่ะ ตอนนี้ทำสำเร็จหรือยัง?',
        'เอ๊ะ? อะไรเนี่ย! แล้วนั่น... ตัวฉันในอนาคตจริงๆ เหรอ? ทำไมดูพรีเมียมจัง! ส่วนเรื่องปารีส... ก็อยากไปนะ แต่ตอนนี้แค่ทำงานทุกวันก็เต็มกลืนแล้วล่ะ',
      ],
      names: ['ตัวฉัน (อนาคต)', 'ตัวฉัน (ปัจจุบัน)'],
      avatarSrcs: ['/images/avatar-future.png', '/images/avatar-surprised.png'],
    },
    {
      title: "ทางแยกของ Talent: จะวิ่งหาหน้ากล้องตลอดไป หรือจะรู้จักให้ 'ภาพถ่าย' ทำงานแทน?",
      imagelabel: 'SCENE 03',
      imagesrc: '/images/scene03.png',
      bubbles: [
        'อนาคตมันขึ้นอยู่กับการตัดสินใจในวันนี้นะ ระหว่าง "คนที่วิ่งรอกแคสติ้งงานทุกวัน" กับ "คนที่รู้จักวางแผนให้รูปทำงานแทน" คุณชอบแบบไหนมากกว่ากันล่ะ?',
        'เอ๋... ถ้าเลือกได้ก็ต้องอยากมีชีวิตที่มั่นคงและมีอิสระอยู่แล้วสิ แต่จะทำยังไงได้ล่ะ? ฉันก็ต้องออกไปถ่ายงานเรื่อยๆ ไม่ใช่เหรอ?',
      ],
      names: ['ตัวฉัน (อนาคต)', 'ตัวฉัน (ปัจจุบัน)'],
      avatarSrcs: ['/images/avatar-future.png', '/images/avatar-thinking.png'],
    },
    {
      title: "เปลี่ยน 'พอร์ต' ให้เป็นรายได้... Nunt บริการที่ Talent ยุคใหม่ต้องมี",
      imagelabel: 'SCENE 04',
      imagesrc: '/images/scene04.png',
      bubbles: [
        'ไม่เสมอไปหรอกจ๊ะ ฉันก็แค่ใช้บริการที่ชื่อว่า "Nunt" เท่านั้นเอง แค่ให้เช่ารูปภาพสวยๆ ที่คุณมีอยู่แล้ว โดยที่เราไม่ต้องออกไปถ่ายงานใหม่เลย',
        'แค่ให้เช่ารูปที่มีอยู่แล้วก็ได้เหรอ? ไม่ต้องเหนื่อยเพิ่ม แถมยังมีรายได้เข้ามาตลอด... แบบนี้ก็น่าลองดูนะ!',
      ],
      names: ['ตัวฉัน (อนาคต)', 'ตัวฉัน (ปัจจุบัน)'],
      avatarSrcs: ['/images/avatar-future.png', '/images/avatar-current.png'],
    },
    {
      title: 'จาก ภาพพอร์ต สู่โอกาสระดับโลก... อนาคตที่อิสระของ Talent เริ่มต้นขึ้นแล้ว',
      imagelabel: 'SCENE 05',
      imagesrc: '/images/scene05.png',
      bubbles: [
        'โหหห! ไม่อยากจะเชื่อเลย ภาพในสโคปนั่น... ฉันกำลังสนุกอยู่ที่ปารีสจริงๆ ด้วย! แถมยังมีงานติดต่อมาไม่ขาดสายเลย ✨',
        'ดีใจด้วยนะที่ตัดสินใจเริ่มในวันนั้น! เพราะคุณเลือก Nunt อนาคตที่สดใสแบบนี้ถึงเกิดขึ้นจริงยังไงล่ะ',
      ],
      names: ['ตัวฉัน (ปัจจุบัน)', 'ตัวฉัน (อนาคต)'],
      avatarSrcs: ['/images/avatar-current.png', '/images/avatar-future.png'],
    },
  ];

  return (
    <div style={{ background: C.bg, minHeight: '100vh' }}>
      <Header isPC={isPC} />

      <main style={{
        maxWidth: isPC ? 760 : 480,
        margin: '0 auto',
        padding: isPC ? '0 40px' : '0',
      }}>
        <div style={{ paddingTop: isPC ? 64 : 48 }}>
          {scenes.map((scene, i) => (
            <React.Fragment key={i}>
              {i === 4 && <NuntInfoBox isPC={isPC} />}
              <Scene {...scene} isPC={isPC} />
              {i < scenes.length - 1 && <Divider isPC={isPC} />}
            </React.Fragment>
          ))}
        </div>

        <CTASection isPC={isPC} />
        <Footer isPC={isPC} />
      </main>
    </div>
  );
}
